import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { graphicsManager } from "appworks/graphics/graphics-manager";
import { particleManager } from "appworks/graphics/particles/particle-manager";
import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { gameState } from "appworks/model/game-state";
import { Services } from "appworks/services/services";
import { CancelGroup } from "appworks/utils/contracts/cancel-group";
import { Contract } from "appworks/utils/contracts/contract";
import { Point } from "appworks/utils/geom/point";
import { RandomRangeInt } from "appworks/utils/math/random";
import { Easing, Interpolation, Tween } from "appworks/utils/tween";
import { TGRSymbolComponent } from "components/tgr-symbol";
import { gameLayers } from "game-layers";
import { TGRInstantWin, TGRInstantWinResult } from "integration/builders/results/tgr-instant-win-result";
import { Emitter } from "pixi-particles";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { TGRSoundEvent } from "tgr-sound-event-enum";

const EASING: (k: number) => number = Easing.Quadratic.Out;

export class DMFInstantWinsComponent extends AbstractComponent {

    protected cancelGroup: CancelGroup = new CancelGroup();

    public animateCollect(instantWinResult: TGRInstantWinResult, instantWin: TGRInstantWin, duration: number): Contract<void> {
        return new Contract<void>((resolve: (value?: any) => void) => {
            const symbol: TGRSymbolComponent = Components.get(MatrixComponent).getSymbol(instantWin.gridPosition.x, instantWin.gridPosition.y) as TGRSymbolComponent;
            const cltPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_${instantWinResult.collectSymbolPosition.x}_${instantWinResult.collectSymbolPosition.y}`);
            const rgtPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_rgt_${RandomRangeInt(0, 2)}`);
            const midPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_mid_${RandomRangeInt(0, 2)}`);
            const symPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_${instantWin.gridPosition.x}_${instantWin.gridPosition.y}`);

            const emitter: Emitter = particleManager.get(particleManager.add("star_particles", gameLayers.InstantWins.container));
            emitter.updateSpawnPos(cltPos.landscape.x + (cltPos.landscape.width / 2), cltPos.landscape.y + (cltPos.landscape.height / 2));

            const tweenPath: { x: number[], y: number[] } = {
                x: [
                    rgtPos.landscape.x + (rgtPos.landscape.width / 2),
                    midPos.landscape.x + (midPos.landscape.width / 2),
                    symPos.landscape.x + (symPos.landscape.width / 2)
                ],
                y: [
                    rgtPos.landscape.y + (rgtPos.landscape.height / 2),
                    midPos.landscape.y + (midPos.landscape.height / 2),
                    symPos.landscape.y + (symPos.landscape.height / 2)
                ]
            };
            const tweenObj: {x: number, y: number} =  {
                x: cltPos.landscape.x + (cltPos.landscape.width / 2),
                y: cltPos.landscape.y + (cltPos.landscape.height / 2)
            };

            const tween: Tween = new Tween(tweenObj);
            tween.to(tweenPath, duration);
            tween.onStart(() => {
                Services.sound.customEvent(TGRSoundEvent.monkey_fizz);
                emitter.emit = true;
            });
            tween.interpolation(Interpolation.Bezier);
            tween.easing(EASING);
            tween.onUpdate(() => emitter.updateSpawnPos(tweenObj.x, tweenObj.y));
            tween.onComplete(() => {
                symbol.setSymbol("QH");
                (gameState.getCurrentGame().getCurrentRecord() as SpinRecord).grid[instantWin.gridPosition.x][instantWin.gridPosition.y] = "QH";
                emitter.emit = false;
                resolve();
            });
            tween.start();
        });
    }

    public animateWin(instantWin: TGRInstantWin, duration: number): Contract<void> {
        return new Contract<void>((resolve: (value?: any) => void) => {

            const gridPosition: Point = instantWin.gridPosition;
            const symPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_${gridPosition.x}_${gridPosition.y}`);
            const midPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_mid_${RandomRangeInt(0, 2)}`);
            const rgtPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_rgt_${RandomRangeInt(0, 2)}`);
            const topPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_top_${RandomRangeInt(0, 2)}`);

            if (symPos && midPos && topPos) {

                const x: number = symPos.landscape.x + (symPos.landscape.width / 2);
                const y: number = symPos.landscape.y + (symPos.landscape.height / 2);

                const coinEmitter: Emitter = particleManager.get(particleManager.add("coin_particles", gameLayers.InstantWins.container));
                coinEmitter.updateSpawnPos(x, y);

                const light: Sprite = graphicsManager.createSprite("light");
                light.visible = false;
                light.tint = 0xFFD700;
                gameLayers.InstantWins.add(light);
                CenterPivot(light);
                light.landscape.scale.set(0.2);
                light.landscape.set(x, y);

                const tweenPath: { x: number[], y: number[] } = {
                    x: [
                        midPos.landscape.x + (midPos.landscape.width / 2),
                        rgtPos.landscape.x + (rgtPos.landscape.width / 2),
                        topPos.landscape.x + (topPos.landscape.width / 2)
                    ],
                    y: [
                        midPos.landscape.y + (midPos.landscape.height / 2),
                        rgtPos.landscape.y + (rgtPos.landscape.height / 2),
                        topPos.landscape.y + (topPos.landscape.height / 2)
                    ]
                };
                const tweenObj: { x: number, y: number } =  { x, y };
                const tween = new Tween(tweenObj);
                tween.to(tweenPath, duration);
                tween.onStart(() => {
                    Services.sound.customEvent(TGRSoundEvent.monkey_collect);
                    coinEmitter.emit = true;
                    this.revealAndSpinLight(light, duration);
                });
                tween.interpolation(Interpolation.Bezier);
                tween.easing(EASING);
                tween.onUpdate(() => {
                    coinEmitter.updateSpawnPos(tweenObj.x, tweenObj.y);
                });
                tween.onComplete(() => {
                    coinEmitter.emit = false;
                    resolve();
                });
                tween.start();
            }
        });
    }

    public setTopValue(value: string, duration: number): void {
        this.cancelGroup.cancel();

        const topText = gameLayers.InstantWins.getText("value");
        topText.text = value;

        this.cancelGroup.tween(
            new Tween(topText.landscape.scale).to({x: 1.5, y: 1.5}, duration * 0.6).chain(
                new Tween(topText.landscape.scale).to({x: 1.0, y: 1.0}, duration * 0.4)
            ).start()
        );
    }

    protected revealAndSpinLight(light: Sprite, duration: number): void {
        light.visible = true;
        light.rotation = Math.PI / 8;
        new Tween(light).to({alpha: 0}, duration).start();
        new Tween(light.landscape.scale).to({x: 1.5, y: 1}, duration * 0.2).chain(
            new Tween(light.landscape.scale).to({x: 0.5, y: 0.1}, duration * 0.6)
        ).start();
    }
}
